import React, { useEffect } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import Layout from './components/Layout/Layout'
import PublicLayout from './components/Layout/PublicLayout'
import ErrorBoundary from './components/ErrorBoundary'
import LoadingSpinner from './components/LoadingSpinner'

// 页面组件
import HomePage from './pages/HomePage'
import LoginPage from './pages/auth/LoginPage'
import PhoneLoginPage from './pages/auth/PhoneLoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import CoursesPage from './pages/courses/CoursesPage'
import CourseDetailPage from './pages/courses/CourseDetailPage'
import TeachersPage from './pages/teachers/TeachersPage'
import TeacherDetailPage from './pages/teachers/TeacherDetailPage'
import TeacherProfileDetail from './pages/teachers/TeacherProfileDetail'
import BookingPage from './pages/booking/BookingPage'
import BookingSuccess from './pages/booking/BookingSuccess'
import DashboardPage from './pages/dashboard/DashboardPage'

// AI功能页面
import { AIPage } from './pages/ai/AIPage'
import { TeacherAIPage } from './pages/teacher/TeacherAIPage'
import { AdminAIPage } from './pages/admin/AdminAIPage'

// 学生端页面
import StudentDashboard from './pages/student/StudentDashboard'
import StudentBookings from './pages/student/StudentBookings'
import StudentMessages from './pages/student/StudentMessages'
import StudentProfile from './pages/student/StudentProfile'
import StudentSchedule from './pages/student/StudentSchedule'
import GroupCourses from './pages/student/GroupCourses'
import AdmissionApplication from './pages/student/AdmissionApplication'
import ExamCenter from './pages/student/ExamCenter'
import ExamDetail from './pages/student/ExamDetail'
import TakeExam from './pages/student/TakeExam'
import ExamResult from './pages/student/ExamResult'
import ExamTaking from './pages/student/ExamTaking'
import KnowledgeBase from './pages/student/KnowledgeBase'
import KnowledgeDetail from './pages/student/KnowledgeDetail'
import WrongAnswerBook from './pages/student/WrongAnswerBook'
import LearningAnalysis from './pages/student/LearningAnalysis'

// 老师端页面
import TeacherDashboard from './pages/teacher/TeacherDashboard'
import TeacherApplication from './pages/teacher/TeacherApplication'
import TeacherSchedule from './pages/teacher/TeacherSchedule'
import TeacherCourses from './pages/teacher/TeacherCourses'
import CreateCourse from './pages/teacher/CreateCourse'
import CreateGroupCourse from './pages/teacher/CreateGroupCourse'
import EditCourse from './pages/teacher/EditCourse'
import TeacherBookingsImproved from './pages/teacher/TeacherBookingsImproved'
import TeacherProfile from './pages/teacher/TeacherProfile'
import TeacherMessages from './pages/teacher/TeacherMessages'
import TeacherRatingDisputes from './pages/teachers/TeacherRatingDisputes'

// 管理员页面
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminUsers from './pages/admin/AdminUsers'
import AdminMessages from './pages/admin/AdminMessages'
import AdminTeachers from './pages/admin/AdminTeachers'
import AdminCourses from './pages/admin/AdminCourses'
import AdminBookings from './pages/admin/AdminBookings'
import TeacherApplications from './pages/admin/TeacherApplications'
import AdminAchievements from './pages/admin/AdminAchievements'
import AdminRatingDisputes from './pages/admin/AdminRatingDisputes'
import AIAdmissionOfficer from './pages/admin/AIAdmissionOfficer'

// 新的页面
import TeacherRegister from './pages/auth/TeacherRegister'
import TeacherApplicationStatus from './pages/teacher/TeacherApplicationStatus'

// 新的课程预约组件
import CourseList from './components/CourseList'
import BookingForm from './components/BookingForm'
import UnauthorizedPage from './components/UnauthorizedPage'

// 仪表板重定向组件
const DashboardRedirect = () => {
  const { user } = useAuthStore()
  
  if (user?.role === 'teacher') {
    return <Navigate to="/teacher/dashboard" replace />
  } else if (user?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />
  } else {
    return <Navigate to="/student/dashboard" replace />
  }
}

const ProfileRedirect = () => {
  const { user } = useAuthStore()

  if (user?.role === 'teacher') {
    return <Navigate to="/teacher/profile" replace />
  } else if (user?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />
  } else {
    return <Navigate to="/student/profile" replace />
  }
}

// 受保护的路由组件
const ProtectedRoute = ({ children, requiredRole }: { children: React.ReactNode, requiredRole?: string }) => {
  const { user, isAuthenticated, isInitializing, clearAll } = useAuthStore()

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="正在加载..." />
      </div>
    )
  }

  // 如果没有认证，重定向到登录页
  if (!isAuthenticated) {
    console.log('ProtectedRoute: 用户未认证，重定向到登录页')
    return <Navigate to="/login" replace />
  }

  // 如果没有用户信息但声称已认证，说明状态不一致，清除并重定向
  if (isAuthenticated && !user) {
    console.log('ProtectedRoute: 认证状态不一致，清除状态')
    clearAll()
    return <Navigate to="/login" replace />
  }

  // 角色权限检查
  if (requiredRole && user?.role !== requiredRole) {
    console.log(`ProtectedRoute: 角色权限不足，需要${requiredRole}，当前${user?.role}`)
    return <Navigate to="/unauthorized" replace />
  }

  return <>{children}</>
}

// 公共路由（未登录用户可访问）
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isInitializing, user, clearAll } = useAuthStore()

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="正在加载..." />
      </div>
    )
  }

  // 如果声称已认证但没有用户信息，说明状态不一致，清除状态并允许访问公共页面
  if (isAuthenticated && !user) {
    console.log('PublicRoute: 认证状态不一致，清除无效状态')
    clearAll()
    return <>{children}</>
  }

  // 如果真的已认证且有用户信息，重定向到仪表板
  if (isAuthenticated && user) {
    console.log('PublicRoute: 用户已认证，重定向到仪表板')
    return <Navigate to="/dashboard" replace />
  }

  // 未认证，正常显示公共页面
  console.log('PublicRoute: 用户未认证，显示公共页面')
  return <>{children}</>
}

function App() {
  const { initialize } = useAuthStore()

  useEffect(() => {
    initialize()
  }, [initialize])

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* 公共页面 */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<HomePage />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="courses/:id" element={<CourseDetailPage />} />
            <Route path="teachers" element={<TeachersPage />} />
            <Route path="teachers/:id" element={<TeacherDetailPage />} />
            <Route path="teachers/:id/profile" element={<TeacherProfileDetail />} />
            
            {/* 认证页面 */}
            <Route path="login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="phone-login" element={
              <PublicRoute>
                <PhoneLoginPage />
              </PublicRoute>
            } />
            <Route path="register" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />
            <Route path="forgot-password" element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            } />
            <Route path="reset-password" element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } />
            <Route path="teacher-register" element={<TeacherRegister />} />
          </Route>

          {/* 受保护的页面 */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<DashboardRedirect />} />
            <Route path="profile" element={<ProfileRedirect />} />
            {/* 学生端路由 */}
            <Route path="student" element={<ProtectedRoute requiredRole="student"><Outlet /></ProtectedRoute>}>
              <Route path="dashboard" element={<StudentDashboard />} />
              <Route path="bookings" element={<StudentBookings />} />
              <Route path="messages" element={<StudentMessages />} />
              <Route path="schedule" element={<StudentSchedule />} />
              <Route path="group-courses" element={<GroupCourses />} />
              <Route path="profile" element={<StudentProfile />} />
              <Route path="ai" element={<AIPage />} />
              <Route path="admission-application" element={<AdmissionApplication />} />
              <Route path="exams" element={<ExamCenter />} />
              <Route path="exams/:examId" element={<ExamDetail />} />
              <Route path="exams/:examId/take" element={<TakeExam />} />
              <Route path="exams/:examId/taking" element={<ExamTaking />} />
              <Route path="exams/:examId/result" element={<ExamResult />} />
              {/* 知识库 */}
              <Route path="knowledge" element={<KnowledgeBase />} />
              <Route path="knowledge/:id" element={<KnowledgeDetail />} />
              {/* 错题本和学习分析 */}
              <Route path="wrong-answers" element={<WrongAnswerBook />} />
              <Route path="learning-analysis" element={<LearningAnalysis />} />
            </Route>

            <Route path="booking/:courseId" element={
              <ProtectedRoute requiredRole="student">
                <BookingForm />
              </ProtectedRoute>
            } />
            <Route path="booking/success" element={
              <ProtectedRoute requiredRole="student">
                <BookingSuccess />
              </ProtectedRoute>
            } />

            {/* 老师端路由 */}
            <Route path="teacher" element={<ProtectedRoute requiredRole="teacher"><Outlet /></ProtectedRoute>}>
              <Route path="dashboard" element={<TeacherDashboard />} />
              <Route path="schedule" element={<TeacherSchedule />} />
              <Route path="courses" element={<TeacherCourses />} />
              <Route path="create-course" element={<CreateCourse />} />
              <Route path="create-group-course" element={<CreateGroupCourse />} />
              <Route path="edit-course/:id" element={<EditCourse />} />
              <Route path="bookings" element={<TeacherBookingsImproved />} />
              <Route path="messages" element={<TeacherMessages />} />
              <Route path="profile" element={<TeacherProfile />} />
              <Route path="rating-disputes" element={<TeacherRatingDisputes />} />
              <Route path="ai" element={<TeacherAIPage />} />
            </Route>

            {/* 管理员路由 */}
            <Route path="admin" element={<ProtectedRoute requiredRole="admin"><Outlet /></ProtectedRoute>}>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="teachers" element={<AdminTeachers />} />
              <Route path="teacher-applications" element={<TeacherApplications />} />
              <Route path="courses" element={<AdminCourses />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="achievements" element={<AdminAchievements />} />
              <Route path="rating-disputes" element={<AdminRatingDisputes />} />
              <Route path="ai" element={<AdminAIPage />} />
              <Route path="ai-admission" element={<AIAdmissionOfficer />} />
            </Route>
          </Route>

          {/* 教师申请状态页面 - 独立布局，不显示侧边栏 */}
          <Route path="/teacher/application-status" element={
            <ProtectedRoute requiredRole="teacher">
              <TeacherApplicationStatus />
            </ProtectedRoute>
          } />

          {/* 教师申请表单页面 - 独立布局，不显示侧边栏 */}
          <Route path="/teacher/application" element={
            <ProtectedRoute requiredRole="teacher">
              <TeacherApplication />
            </ProtectedRoute>
          } />

          {/* Unauthorized页面 */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* 404页面 */}
          <Route path="*" element={
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600 mb-8">页面不存在</p>
                <a 
                  href="/" 
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  返回首页
                </a>
              </div>
            </div>
          } />
        </Routes>
      </div>
    </ErrorBoundary>
  )
}

export default App